/**
 * trackEvent — critical-area error monitoring for the Footprints parent app.
 *
 * Only tracks errors in: support, cctv, reload, login, other.
 *
 * Usage:
 *   trackEvent({ category: 'support', title: 'Take a Break failed', endpoint, message });
 *
 * Rules:
 *  - NEVER await — fire and forget
 *  - Persist in SQLite (jeep-sqlite in-memory on web); memory fallback if plugin missing
 *  - Batch flush: every 1 hour OR when 10 events are queued
 *  - Same error type pushed at most 3 times in 4 hours
 *  - Failed POST keeps events queued for the next flush
 */

import { axiosClient } from '../axios';
import Constants from '../constant';
import { Capacitor } from '@capacitor/core';
import { classifyErrorCategory, classifyOtherError, isCriticalCategory } from './errorCategories';
import { getActiveEventContext, getLoginEventContext } from './eventContext';
import { deriveSupportFeatureTitle } from './supportFeatureTitles';
import {
    BATCH_SIZE,
    FLUSH_INTERVAL_MS,
    dropRateLimitedPending,
    enqueueEvent,
    ensureEventStore,
    getErrorType,
    getEventStoreBackend,
    getPendingCount,
    getRemainingPushSlots,
    markEventsPushed,
    peekPendingEvents,
    wouldExceedRateLimit,
} from './eventStore';

let flushTimer = null;
let flushing = false;
let schedulerStarted = false;

function generateUUID() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
        const bytes = new Uint8Array(16);
        crypto.getRandomValues(bytes);
        bytes[6] = (bytes[6] & 0x0f) | 0x40;
        bytes[8] = (bytes[8] & 0x3f) | 0x80;
        const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
        return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = (Math.random() * 16) | 0;
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
}

function getEnvironment() {
    const t = Constants?.env?.type;
    if (!t) return 'unknown';
    return t.toLowerCase();
}

function getPlatform() {
    try {
        return Capacitor.getPlatform();
    } catch {
        return 'web';
    }
}

function formatSubtitle(endpoint, method) {
    if (!endpoint) return undefined;
    const verb = (method || 'POST').toUpperCase();
    return `${verb} ${endpoint}`;
}

const CATEGORY_TITLES = {
    login:   'Login error',
    cctv:    'CCTV error',
    reload:  'Reload error',
    support: 'Support error',
    other:   'Other error',
};

const TITLE_MAX_LEN = 120;

/** Title = short scan label; message = full API / detail text. */
function resolveTitleAndMessage(partial, category) {
    const message = String(partial.message ?? partial.title ?? '').trim();
    if (!message) return null;

    const explicitTitle = partial.title ? String(partial.title).trim() : '';

    if (explicitTitle && explicitTitle !== message) {
        return {
            title:   explicitTitle.slice(0, 255),
            message: message.slice(0, 1000),
        };
    }

    if (category === 'support') {
        const featureTitle = deriveSupportFeatureTitle(
            partial.endpoint,
            partial.requestData
        );
        if (featureTitle) {
            return {
                title:   featureTitle.slice(0, 255),
                message: message.slice(0, 1000),
            };
        }
    }

    if (message.length <= TITLE_MAX_LEN) {
        return {
            title:   message.slice(0, 255),
            message: message.slice(0, 1000),
        };
    }

    const shortTitle = CATEGORY_TITLES[category] || 'Error';
    return {
        title:   shortTitle.slice(0, 255),
        message: message.slice(0, 1000),
    };
}

/**
 * Keep each error type within 3 pushes / 4 hours.
 * Returns { toPush, toDrop }.
 */
async function splitByRateLimit(rows) {
    const remainingByType = new Map();
    const toPush = [];
    const toDrop = [];

    for (const row of rows) {
        const type = row.errorType;
        if (!remainingByType.has(type)) {
            remainingByType.set(type, await getRemainingPushSlots(type));
        }
        const left = remainingByType.get(type);
        if (left <= 0) {
            toDrop.push(row);
            continue;
        }
        remainingByType.set(type, left - 1);
        toPush.push(row);
    }

    return { toPush, toDrop };
}

async function doFlush() {
    if (flushing) return;
    flushing = true;
    try {
        await ensureEventStore();

        // Flush in batches of BATCH_SIZE until queue is empty or a POST fails.
        while (true) {
            const rows = await peekPendingEvents(BATCH_SIZE);
            if (rows.length === 0) break;

            const { toPush, toDrop } = await splitByRateLimit(rows);
            if (toDrop.length) {
                await dropRateLimitedPending(toDrop);
            }
            if (toPush.length === 0) break;

            const batch = toPush.map((r) => r.event);
            try {
                await axiosClient.post('/monitoring/events', { events: batch });
                await markEventsPushed(toPush);
            } catch (e) {
                console.warn('[trackEvent] flush failed:', e?.message);
                break;
            }

            if (rows.length < BATCH_SIZE) break;
        }
    } catch (e) {
        console.warn('[trackEvent] flush error:', e?.message);
    } finally {
        flushing = false;
    }
}

function scheduleImmediateFlush() {
    // Coalesce rapid enqueues into one microtask flush.
    Promise.resolve().then(() => doFlush());
}

/** Start the hourly flush timer (idempotent). Call once at app boot. */
export function startEventFlushScheduler() {
    if (schedulerStarted) return;
    schedulerStarted = true;

    ensureEventStore().catch(() => {});

    if (flushTimer) clearInterval(flushTimer);
    flushTimer = setInterval(() => {
        doFlush();
    }, FLUSH_INTERVAL_MS);

    // Flush any leftover queue shortly after boot (e.g. prior session memory N/A; native DB may have rows).
    setTimeout(() => doFlush(), 5000);

    // Browser console: await __monitoringQueue.list()
    if (typeof window !== 'undefined') {
        window.__monitoringQueue = {
            count: () => getQueuedEventCount(),
            list: async (limit = 50) => {
                const rows = await peekPendingEvents(limit);
                return rows.map((r) => ({ id: r.id, errorType: r.errorType, ...r.event }));
            },
            flush: () => flushEventsNow(),
            backend: () => getEventStoreBackend(),
        };
    }
}

/**
 * Queue one critical-area error event.
 *
 * Required: title or message
 * Required (direct or via endpoint): category — support | cctv | reload | login | other
 */
export function trackEvent(partial) {
    try {
        if (!partial?.title && !partial?.message) {
            console.warn('[trackEvent] skipped — missing title or message', partial);
            return;
        }

        const message = String(partial.message ?? partial.title ?? '').trim();
        const category = partial.category
            || classifyErrorCategory(partial.endpoint)
            || classifyOtherError(message);
        if (!isCriticalCategory(category)) {
            return;
        }

        const resolved = resolveTitleAndMessage(partial, category);
        if (!resolved) return;

        const ctx = getActiveEventContext();
        const loginCtx = category === 'login' ? getLoginEventContext() : {};

        const event = {
            id:          generateUUID(),
            timestamp:   new Date().toISOString(),
            environment: getEnvironment(),
            platform:    getPlatform(),
            tab:         'errors',
            severity:    'error',
            category,
            feature:     category,
            statusLabel: partial.statusLabel || 'Error',
            subtitle:    partial.subtitle ?? formatSubtitle(partial.endpoint, partial.method),
            ...ctx,
            ...loginCtx,
            ...partial,
            title:       resolved.title,
            message:     resolved.message,
            category,
            feature:     category,
            childId:     partial.childId ?? ctx.childId,
            centerId:    partial.centerId ?? ctx.centerId,
            centerName:  partial.centerName ?? ctx.centerName,
            parentType:  partial.parentType ?? ctx.parentType,
            loginContact: partial.loginContact ?? loginCtx.loginContact,
            loginMethod:  partial.loginMethod ?? loginCtx.loginMethod,
        };

        const errorType = getErrorType(event);

        // Fire-and-forget enqueue + maybe flush
        (async () => {
            try {
                if (await wouldExceedRateLimit(errorType)) {
                    console.warn('[trackEvent] rate-limited (max 3 / 4h):', errorType);
                    return;
                }

                const pendingCount = await enqueueEvent(event);
                if (pendingCount >= BATCH_SIZE) {
                    scheduleImmediateFlush();
                }
            } catch (e) {
                console.warn('[trackEvent] enqueue failed:', e?.message);
            }
        })();
    } catch (e) {
        console.warn('[trackEvent] failed:', e?.message);
    }
}

/** Track HTTP 200 responses where the API body reports status: "error". */
export function trackApiResponseError(data, { endpoint, method, title, requestData } = {}) {
    if (data?.status !== 'error') return;

    // Handled silently elsewhere (token refresh / reload retry)
    if (data?.errorCode === 'AUTH_EXPIRED') return;

    const message = data?.error || data?.message;
    if (!message) return;

    trackEvent({
        endpoint,
        method,
        title,
        message,
        requestData,
    });
}

/** Exposed for tests / manual flush. */
export function flushEventsNow() {
    return doFlush();
}

export async function getQueuedEventCount() {
    return getPendingCount();
}
